import { CompositeLayer } from '@deck.gl/core'
import { LabeledIconLayer } from 'ifield-core-components'
import Supercluster from 'supercluster'
import _ from 'lodash'
import { CLUSTER_RADIUS } from '@/constants'
import { getUserTypeFromCluster } from '@/js/util/clusterUtil'

// クラスターの最大数要素数
const MAX_LEAVE = Infinity

const defaultProps = {
  ...LabeledIconLayer.defaultProps,
  // props for clustering setting
  clustering: { type: 'boolean', value: true },
  // props for supercluster
  minZoom: { type: 'number', value: 0 },
  maxZoom: { type: 'number', value: 22 },
  minPoints: { type: 'number', value: 2 },
  radius: { type: 'number', value: CLUSTER_RADIUS },
  tileExtent: { type: 'number', value: 256 },
  nodeSize: { type: 'number', value: 64 },
  zoom: { type: 'number', value: 0 },
  // props for event
  onClustered: { type: 'function', value: (d) => d },
}

class IconClusterLayer extends CompositeLayer {
  shouldUpdateState({ changeFlags }) {
    return changeFlags.somethingChanged
  }

  updateState({ props, oldProps, changeFlags }) {
    const { clustering } = props
    const clusteringChanged = oldProps.clustering !== clustering

    if (!clustering) {
      // クラスタリングが無効な場合、以下の条件時のみ処理を実行
      // - クラスタリングの有効/無効が切り替わった
      // - 元データが変更された場合
      if (clusteringChanged || changeFlags.dataChanged) {
        // 元データをLabeledIconLayerが直接描画できる形式に変換
        const data = props.data.map((d) => ({
          geometry: { coordinates: props.getPosition(d) },
          properties: d,
        }))
        // state更新:
        // - data: 変換された描画用データ
        // - index: クラスタリングが無効なのでSuperclusterのインデックスは不要
        // - z: ズームレベルもクラスタリングにのみ関連するため不要
        this.setState({ data, index: null, z: null })
        props.onClustered(data)
      }
      return
    }
    const propsToUpdateCluster = [
      'minZoom',
      'maxZoom',
      'minPoints',
      'radius',
      'tileExtent',
      'nodeSize',
    ]
    const rebuildIndex =
      changeFlags.dataChanged ||
      clusteringChanged ||
      propsToUpdateCluster.some((key) => props[key] !== oldProps[key])

    if (rebuildIndex) {
      const index = new Supercluster({
        minZoom: props.minZoom,
        maxZoom: props.maxZoom,
        minPoints: props.minPoints,
        radius: props.radius,
        extent: props.tileExtent,
        nodeSize: props.nodeSize,
      })
      // v9対応: getPosition が関数か定数かを確認して呼び出す
      const { getPosition } = props
      index.load(
        props.data.map((d) => ({
          geometry: {
            coordinates:
              typeof getPosition === 'function' ? getPosition(d) : getPosition,
          },
          properties: d,
        }))
      )
      this.setState({ index })
    }

    // ズームレベルが変更されたか、インデックスが再構築された場合にクラスターを更新する
    const z = Math.floor(props.zoom || this.context.viewport.zoom)
    if (rebuildIndex || z !== this.state.z) {
      // 現在の表示範囲とズームレベルからクラスターを取得
      const clusters = this.state.index.getClusters([-180, -85, 180, 85], z)
      const fixedClusters = clusters.map((cluster) => {
        if (!cluster.properties.cluster) return cluster
        // 座標修正
        const fixedCluster = this._fixClusterCoordinates(cluster)
        // properties に children を追加
        const children = this.state.index
          .getLeaves(cluster.properties.cluster_id, MAX_LEAVE)
          .map((child) => child.properties)
        fixedCluster.properties.children = children
        // user_type をプロパティに格納
        fixedCluster.properties.user_type = getUserTypeFromCluster(children)
        return fixedCluster
      })
      this.setState({
        data: fixedClusters,
        z,
      })
      props.onClustered(fixedClusters)
    }
  }

  /**
   * クラスターの座標修正
   * - すべて同じ座標のクラスターは座標を元に戻す
   * @param {Object} clusters クラスターデータ
   * @returns {Object} 座標修正したクラスターデータ
   */
  _fixClusterCoordinates(cluster) {
    if (!cluster.properties.cluster) return cluster
    // v9対応: getPosition の呼び出しを安全にする
    const { getPosition } = this.props

    const positions = this.state.index
      .getLeaves(cluster.properties.cluster_id, MAX_LEAVE)
      .map((f) =>
        typeof getPosition === 'function'
          ? getPosition(f.properties)
          : getPosition
      )

    const isAllPositionsSame = !!positions.reduce((prev, current) =>
      _.isEqual(prev, current) ? current : null
    )
    if (isAllPositionsSame) {
      cluster.geometry.coordinates = [...positions[0]]
    }
    return cluster
  }

  getPickingInfo({ info }) {
    if (info.object && this.props.clustering) {
      const properties = info.object.properties
      if (properties.cluster) {
        properties.children = this.state.index
          .getLeaves(properties.cluster_id, MAX_LEAVE)
          .map((f) => f.properties)
        properties.expansion_zoom = this.state.index.getClusterExpansionZoom(
          properties.cluster_id
        )
      }
    }
    return info
  }

  renderLayers() {
    const { getTextSize, parameters = {}, clustering } = this.props
    return new LabeledIconLayer(
      this.props,
      this.getSubLayerProps({
        id: 'icon',
        data: this.state.data,
        // v9対応: テキストの垂直方向の配置基準を中央に設定
        getTextAlignmentBaseline: 'center',
        getText: (d) =>
          clustering && d.properties.cluster
            ? `${d.properties.point_count}`
            : '',
        getTextSize: (d) => {
          if (!clustering || !d.properties.cluster) return 0
          return typeof getTextSize === 'function'
            ? getTextSize(d)
            : getTextSize
        },
        getPosition: (d) => d.geometry.coordinates,
        _subLayerProps: {
          icon: {
            parameters: {
              ...parameters,
            },
          },
        },
      })
    )
  }
}

IconClusterLayer.layerName = 'IconClusterLayer'
IconClusterLayer.defaultProps = defaultProps

export { IconClusterLayer }
